import { useState, useEffect } from 'react';

// Mobile breakpoints
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

// Mobile detection hook
export const useIsMobile = (breakpoint: number = breakpoints.md) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
};

// Responsive column configuration for Ant Design Grid
export const getResponsiveColumns = () => ({
  xs: 24,
  sm: 12,
  md: 8,
  lg: 6,
  xl: 4,
});

export const getResponsiveColumnsWide = () => ({
  xs: 24,
  sm: 24,
  md: 12,
  lg: 8,
  xl: 6,
});

// Responsive table columns helper
export const getMobileTableColumns = <T>(
  columns: any[],
  isMobile: boolean,
  essentialKeys: string[] = []
) => {
  if (!isMobile) return columns;
  
  return columns.filter(col => 
    essentialKeys.includes(col.key) || 
    col.responsive?.includes('sm') !== false
  );
};

// Mobile-friendly button configuration
export const getMobileButtonProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('middle' as const),
  style: isMobile ? { marginBottom: 8 } : {},
});

// Responsive Space direction
export const getSpaceDirection = (isMobile: boolean) => 
  isMobile ? 'vertical' : 'horizontal';

// Mobile-friendly pagination
export const getMobilePagination = (isMobile: boolean) => ({
  pageSize: isMobile ? 5 : 10,
  showSizeChanger: !isMobile,
  showQuickJumper: !isMobile,
  simple: isMobile,
  size: isMobile ? 'small' : 'default',
});

// Mobile-friendly table props
export const getMobileTableProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('middle' as const),
  scroll: isMobile ? { x: 'max-content' } : undefined,
  pagination: getMobilePagination(isMobile),
});

// Mobile-friendly card props
export const getMobileCardProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('default' as const),
  style: isMobile ? { marginBottom: 12 } : { marginBottom: 16 },
});

// Mobile-friendly form layout
export const getMobileFormLayout = (isMobile: boolean) => ({
  layout: isMobile ? ('vertical' as const) : ('horizontal' as const),
  labelCol: isMobile ? undefined : { span: 6 },
  wrapperCol: isMobile ? undefined : { span: 18 },
});

// Mobile-friendly input sizes
export const getMobileInputProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('middle' as const),
  style: { width: isMobile ? '100%' : undefined },
});

// Mobile-friendly statistic props
export const getMobileStatisticProps = (isMobile: boolean) => ({
  valueStyle: {
    fontSize: isMobile ? '16px' : '24px',
    textAlign: isMobile ? 'center' : 'left',
  },
});

// Export all utilities
export const ResponsiveUtils = {
  useIsMobile,
  getResponsiveColumns,
  getResponsiveColumnsWide,
  getMobileTableColumns,
  getMobileButtonProps,
  getSpaceDirection,
  getMobilePagination,
  getMobileTableProps,
  getMobileCardProps,
  getMobileFormLayout,
  getMobileInputProps,
  getMobileStatisticProps,
  breakpoints,
}; 